/*
 * Combined Listings variant picker
 * --------------------------------
 * Drives <variant-selects-combined>, used ONLY by the `main-product-combined`
 * section (the "combined-listing" product template). It is intentionally a
 * separate custom element from the theme's legacy <variant-selects>
 * (assets/global.js) so existing products are completely unaffected.
 *
 * Combined Listings primer (see Shopify docs):
 *   - `product.variants` is capped at 250, so we never read a full variant blob.
 *   - Each option value carries `data-option-value-id` and `data-product-url`.
 *   - Selecting a value whose `product_url` differs from the current product is a
 *     sibling switch: we must load that child product.
 *   - We use the Section Rendering API:
 *       GET <product_url>?section_id=<id>&option_values=<id1,id2,...>
 *     and swap in the returned section HTML. That single request returns the
 *     correct price, media, description, add-to-cart, availability, SKU,
 *     inventory and structured data for the resolved child + variant.
 */
class VariantSelectsCombined extends HTMLElement {
  constructor() {
    super();
    this.onVariantChange = this.onVariantChange.bind(this);
    this.addEventListener('change', this.onVariantChange);
  }

  connectedCallback() {
    this.sectionId = this.dataset.section;
  }

  get sectionElement() {
    return document.getElementById(`MainProduct-${this.dataset.section}`);
  }

  // Collect the currently-selected option_value ids, in option order. Each
  // option group is either a <fieldset> (radio / swatch pickers) or a <select>
  // (dropdown / swatch_dropdown picker); walking them in document order keeps
  // the ids aligned with the option positions even when picker types are mixed.
  getSelectedOptionValueIds() {
    const ids = [];
    this.querySelectorAll('fieldset, select').forEach((group) => {
      let selected;
      if (group.tagName === 'SELECT') {
        selected = group.selectedOptions && group.selectedOptions[0];
      } else {
        selected = group.querySelector('input:checked');
      }
      if (selected && selected.dataset.optionValueId) ids.push(selected.dataset.optionValueId);
    });
    return ids;
  }

  // The product_url tied to the value the shopper just chose. For a <select>
  // that's the selected <option>; for a radio it's the input itself.
  getTargetFromEvent(target) {
    if (target.tagName === 'SELECT') return target.selectedOptions && target.selectedOptions[0];
    return target;
  }

  onVariantChange(event) {
    const changed = this.getTargetFromEvent(event.target);
    const oldProductUrl = this.dataset.url;
    const newProductUrl = (changed && changed.dataset.productUrl) || oldProductUrl;
    const selectedIds = this.getSelectedOptionValueIds();

    const params = selectedIds.length ? `&option_values=${selectedIds.join(',')}` : '';
    const requestUrl = `${newProductUrl}?section_id=${this.dataset.section}${params}`;

    // Race guard: ignore the response of any request superseded by a newer one.
    const requestToken = (this._requestToken = (this._requestToken || 0) + 1);

    const section = this.sectionElement;
    if (section) section.setAttribute('aria-busy', 'true');

    fetch(requestUrl)
      .then((response) => {
        if (!response.ok) throw new Error(`Section render failed: ${response.status}`);
        return response.text();
      })
      .then((text) => {
        if (requestToken !== this._requestToken) return; // a newer selection won

        const html = new DOMParser().parseFromString(text, 'text/html');
        this.swapSection(html, { newProductUrl, oldProductUrl, focusId: event.target.id });
      })
      .catch((error) => {
        console.error('[combined-listings]', error);
      })
      .finally(() => {
        const live = this.sectionElement;
        if (live) live.removeAttribute('aria-busy');
      });
  }

  swapSection(html, { newProductUrl, oldProductUrl, focusId }) {
    const sectionId = this.dataset.section;
    const newSection = html.getElementById(`MainProduct-${sectionId}`);
    const liveSection = document.getElementById(`MainProduct-${sectionId}`);
    if (!newSection || !liveSection) return;

    // Replacing innerHTML in the LIVE document upgrades the nested custom
    // elements (<product-info>, <gallery-product>, <variant-selects-combined>,
    // <product-form>, price-per-item, volume-pricing, ...) so they all
    // re-initialise for the new product/variant.
    liveSection.innerHTML = newSection.innerHTML;

    // Keep SEO structured data in sync on a sibling switch (it lives outside the
    // <section>, so innerHTML alone won't catch it).
    const newSchema = html.getElementById(`ProductSchema-${sectionId}`);
    const liveSchema = document.getElementById(`ProductSchema-${sectionId}`);
    if (newSchema && liveSchema) liveSchema.textContent = newSchema.textContent;

    // Update the address bar so refresh / share resolves the correct child +
    // variant. The resolved variant id is the new add-to-cart hidden input.
    if (this.dataset.updateUrl !== 'false') {
      const variantInput = liveSection.querySelector('input[name="id"]');
      const variantParam = variantInput && variantInput.value ? `?variant=${variantInput.value}` : '';
      window.history.replaceState({}, '', `${newProductUrl}${variantParam}`);
    }

    // Notify the rest of the page (e.g. anything subscribed to variant changes).
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.variantChange, { data: { sectionId, html, productUrl: newProductUrl } });
    }

    // Restore focus to the equivalent control in the freshly rendered picker.
    if (focusId) {
      const refocus = document.getElementById(focusId);
      if (refocus) refocus.focus();
    }
  }
}

if (!customElements.get('variant-selects-combined')) {
  customElements.define('variant-selects-combined', VariantSelectsCombined);
}
